/**
 * The page's side of the content pass (2026-09-14).
 *
 * `generateCRFVariables` now returns, beside `variables` and `structure`, a
 * `content` object: the grounded margin notes and the pass's provenance.
 * This module applies it to the form the CRF Builder holds and maps the
 * provenance onto the save payload. Pure, so `npm run test:web` can reach it
 * (`web` has no test runner of its own).
 *
 * THE CONTRACT THAT MATTERS: a form with no content contributes NOTHING and
 * is returned untouched, so every path that never ran the pass is unchanged.
 */

export interface ClientContentRecord {
  source: 'model' | 'none';
  notesWritten?: number;
  refsResolved?: number;
  refsDropped?: number;
  fractionAnnotated?: number;
  fallbackReason?: string;
}

/** The `content` object returned by generateCRFVariables. Untrusted. */
export interface ClientContentResult {
  source: 'model' | 'none';
  record: ClientContentRecord;
  formInstructions?: string;
  formProtocolRef?: string;
  sections?: Array<{ title: string; instructions?: string; protocolRef?: string }>;
  questions?: Array<{ varName: string; guidance?: string; guidanceSource?: string; protocolRef?: string }>;
}

export interface ContentPayloadFields {
  contentSource?: 'model' | 'none';
  contentNotesWritten?: number;
  contentRefsResolved?: number;
  contentRefsDropped?: number;
  contentFractionAnnotated?: number;
  contentFallbackReason?: string;
}

/** Provenance onto the saveCrfForm payload. Nothing in, nothing out. */
export function contentPayloadFields(record: ClientContentRecord | null | undefined): ContentPayloadFields {
  if (!record || typeof record !== 'object' || !record.source) return {};
  if (record.source === 'none') {
    return { contentSource: 'none', ...(record.fallbackReason ? { contentFallbackReason: record.fallbackReason } : {}) };
  }
  const out: ContentPayloadFields = { contentSource: 'model' };
  if (typeof record.notesWritten === 'number') out.contentNotesWritten = record.notesWritten;
  if (typeof record.refsResolved === 'number') out.contentRefsResolved = record.refsResolved;
  if (typeof record.refsDropped === 'number') out.contentRefsDropped = record.refsDropped;
  if (typeof record.fractionAnnotated === 'number') out.contentFractionAnnotated = record.fractionAnnotated;
  return out;
}

type VariableLike = { varName: string; guidance?: string; guidanceSource?: string; protocolRef?: string } & Record<string, any>;
type SectionLike = { title: string; instructions?: string; protocolRef?: string } & Record<string, any>;
type FormLike = {
  variables?: VariableLike[] | null;
  sections?: SectionLike[];
  structure?: ({ instructions?: string } & Record<string, any>) | undefined;
  content?: ClientContentRecord;
} & Record<string, any>;

const fold = (s: string) => String(s ?? '').trim().toLowerCase().replace(/\s+/g, ' ');

/**
 * Apply the pass's output to the form: each note onto its variable, each
 * instruction and citation onto its section, the form-level line appended to
 * whatever the structure pass already wrote, and the provenance onto the form.
 * A 'none' result records only the provenance.
 */
export function applyContentToForm<T extends FormLike>(
  form: T,
  content: ClientContentResult | null | undefined,
): T & { content?: ClientContentRecord } {
  if (!content || typeof content !== 'object' || !content.record) return form;
  if (content.source !== 'model') return { ...form, content: content.record };

  const byVar = new Map((content.questions ?? []).map((q) => [fold(q.varName), q]));
  const variables = Array.isArray(form.variables)
    ? form.variables.map((v) => {
        const note = byVar.get(fold(v.varName));
        if (!note) return v;
        return {
          ...v,
          ...(note.guidance ? { guidance: note.guidance } : {}),
          ...(note.guidanceSource ? { guidanceSource: note.guidanceSource } : {}),
          ...(note.protocolRef ? { protocolRef: note.protocolRef } : {}),
        };
      })
    : form.variables;

  const bySection = new Map((content.sections ?? []).map((s) => [fold(s.title), s]));
  const sections = Array.isArray(form.sections)
    ? form.sections.map((s) => {
        const note = bySection.get(fold(s.title));
        if (!note) return s;
        return {
          ...s,
          ...(note.instructions ? { instructions: note.instructions } : {}),
          ...(note.protocolRef ? { protocolRef: note.protocolRef } : {}),
        };
      })
    : form.sections;

  // The form-level line, and — when the form has no sections of its own for
  // a section instruction to sit under (a generated form usually has none;
  // the pass then reports one section, titled for the form) — that
  // instruction too, so it is not lost. Each line is appended once.
  const formLines: string[] = [];
  if (content.formInstructions) formLines.push(content.formInstructions.trim());
  if (!Array.isArray(form.sections) || form.sections.length === 0) {
    // At most one: a single instruction can stand for the whole form, but two
    // (Inclusion "answer Yes", Exclusion "answer No") concatenated contradict
    // each other, and neither has a section to sit under. Measured on XYZ-002
    // Eligibility Criteria, 2026-09-14.
    const withText = (content.sections ?? []).filter((s) => s.instructions);
    if (withText.length === 1) formLines.push(String(withText[0].instructions).trim());
  }
  let structure = form.structure;
  if (formLines.length > 0) {
    let instructions = String(structure?.instructions ?? '').trim();
    for (const line of formLines) {
      if (!line || instructions.includes(line)) continue;
      instructions = instructions ? `${instructions} ${line}` : line;
    }
    structure = { ...(structure ?? {}), instructions };
  }

  return { ...form, variables, sections, structure, content: content.record };
}
